import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { AdjustmentScenario } from '@/types/api'

interface AdjustmentCardProps {
  adjustment: AdjustmentScenario
  onEdit: (adjustment: AdjustmentScenario) => void
  onDelete: (id: string) => void
  onSelect: (id: string, selected: boolean) => void
  isSelected: boolean
}

/**
 * AdjustmentCard - Card component displaying a single adjustment scenario
 * 
 * Features:
 * - Shows adjustment name, description, and summary
 * - Edit and delete actions
 * - Checkbox for selection (max 3 for scenario runs)
 * - Expanded view showing all position changes
 * - Delete confirmation dialog
 */
export function AdjustmentCard({
  adjustment,
  onEdit,
  onDelete,
  onSelect,
  isSelected,
}: AdjustmentCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = () => {
    onDelete(adjustment.id)
    setShowDeleteDialog(false)
  }

  // Position count
  const positionCount = adjustment.changes.length
  const totalDelta = adjustment.changes.reduce((sum, change) => {
    // Parse newQuantity as number (it's a Decimal string from backend)
    return sum + parseFloat(change.newQuantity || '0')
  }, 0)

  return (
    <>
      <Card className={`relative ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelect(adjustment.id, checked as boolean)}
                className="mt-1"
                aria-label={`Select ${adjustment.name} for scenario run`}
              />
              <div>
                <CardTitle className="text-lg font-semibold">
                  {adjustment.name}
                </CardTitle>
                {adjustment.description && (
                  <p className="text-sm text-gray-600 mt-1">
                    {adjustment.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(adjustment)}
                className="h-8 w-8 p-0"
                aria-label={`Edit ${adjustment.name}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                </svg>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label={`Delete ${adjustment.name}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Summary */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Badge variant="secondary" className="font-normal">
              {positionCount} position{positionCount !== 1 ? 's' : ''} changed
            </Badge>
            <span>•</span>
            <span>Total: {totalDelta.toFixed(4)} units</span>
          </div>

          {/* Expand/Collapse */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? 'Hide details' : 'Show position changes'}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`ml-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </Button>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-3 space-y-2 border-t pt-3">
              <p className="text-sm font-medium text-gray-700">Position Changes:</p>
              {adjustment.changes.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No position changes defined</p>
              ) : (
                <ul className="space-y-1">
                  {adjustment.changes.map((change) => (
                    <li
                      key={change.assetId}
                      className="flex items-center justify-between text-sm py-1 px-2 bg-gray-50 rounded"
                    >
                      <span className="font-medium">{change.symbol}</span>
                      <span className="text-gray-600">
                        → {parseFloat(change.newQuantity).toFixed(4)} units
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Adjustment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{adjustment.name}&quot;? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AdjustmentCard
